import type { ColorToken } from '@hearth/shared';

/**
 * Static Tailwind classes for every configurable color token (stages, relationships, tiers, tags).
 * Strings MUST stay literal so Tailwind's scanner generates them — never build class names dynamically.
 */
export type ColorClasses = {
  /** Soft badge: background + text + border. */
  badge: string;
  /** Strong badge (e.g. the hottest tier): solid background, white text. */
  solid: string;
  /** Small status dot background. */
  dot: string;
  /** Plain text color for inline emphasis. */
  text: string;
};

const COLOR_CLASSES: Record<ColorToken, ColorClasses> = {
  slate: {
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    solid: 'bg-slate-600 text-white border-slate-600 dark:bg-gray-600 dark:border-gray-600',
    dot: 'bg-slate-400',
    text: 'text-slate-600',
  },
  gray: {
    badge: 'bg-gray-100 text-gray-700 border-gray-200',
    solid: 'bg-gray-600 text-white border-gray-600',
    dot: 'bg-gray-400',
    text: 'text-gray-600',
  },
  red: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    solid: 'bg-red-600 text-white border-red-600',
    dot: 'bg-red-500',
    text: 'text-red-600',
  },
  orange: {
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    solid: 'bg-orange-600 text-white border-orange-600',
    dot: 'bg-orange-500',
    text: 'text-orange-600',
  },
  amber: {
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    solid: 'bg-amber-500 text-white border-amber-500',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
  },
  yellow: {
    badge: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    solid: 'bg-yellow-500 text-yellow-950 border-yellow-500',
    dot: 'bg-yellow-400',
    text: 'text-yellow-700',
  },
  lime: {
    badge: 'bg-lime-50 text-lime-800 border-lime-200',
    solid: 'bg-lime-600 text-white border-lime-600',
    dot: 'bg-lime-500',
    text: 'text-lime-700',
  },
  green: {
    badge: 'bg-green-50 text-green-700 border-green-200',
    solid: 'bg-green-600 text-white border-green-600',
    dot: 'bg-green-500',
    text: 'text-green-700',
  },
  emerald: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    solid: 'bg-emerald-600 text-white border-emerald-600',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
  },
  teal: {
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    solid: 'bg-teal-600 text-white border-teal-600',
    dot: 'bg-teal-500',
    text: 'text-teal-700',
  },
  sky: {
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    solid: 'bg-sky-600 text-white border-sky-600',
    dot: 'bg-sky-500',
    text: 'text-sky-700',
  },
  blue: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    solid: 'bg-blue-600 text-white border-blue-600',
    dot: 'bg-blue-500',
    text: 'text-blue-700',
  },
  indigo: {
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    solid: 'bg-indigo-600 text-white border-indigo-600',
    dot: 'bg-indigo-500',
    text: 'text-indigo-700',
  },
  violet: {
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    solid: 'bg-violet-600 text-white border-violet-600',
    dot: 'bg-violet-500',
    text: 'text-violet-700',
  },
  purple: {
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    solid: 'bg-purple-600 text-white border-purple-600',
    dot: 'bg-purple-500',
    text: 'text-purple-700',
  },
  pink: {
    badge: 'bg-pink-50 text-pink-700 border-pink-200',
    solid: 'bg-pink-600 text-white border-pink-600',
    dot: 'bg-pink-500',
    text: 'text-pink-700',
  },
  rose: {
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    solid: 'bg-rose-600 text-white border-rose-600',
    dot: 'bg-rose-500',
    text: 'text-rose-700',
  },
};

function isColorToken(token: string): token is ColorToken {
  return Object.hasOwn(COLOR_CLASSES, token);
}

/** Classes for a color token coming from the API (stored as free text); unknown tokens fall back to slate. */
export function colorClasses(token: string | null | undefined): ColorClasses {
  return token && isColorToken(token) ? COLOR_CLASSES[token] : COLOR_CLASSES.slate;
}

/**
 * Calendar entry fill (time-grid blocks, all-day and multi-day bars, drag previews): tinted background, readable
 * text, and the border color used for the block's left stripe or a note's dashed outline. Literal for the same
 * reason as above.
 */
const CALENDAR_BLOCK_CLASSES: Record<ColorToken, string> = {
  slate: 'bg-slate-100 text-slate-800 border-slate-400 hover:bg-slate-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-400 hover:bg-gray-200',
  red: 'bg-red-100 text-red-900 border-red-500 hover:bg-red-200',
  orange: 'bg-orange-100 text-orange-900 border-orange-500 hover:bg-orange-200',
  amber: 'bg-amber-100 text-amber-900 border-amber-500 hover:bg-amber-200',
  yellow: 'bg-yellow-100 text-yellow-900 border-yellow-500 hover:bg-yellow-200',
  lime: 'bg-lime-100 text-lime-900 border-lime-500 hover:bg-lime-200',
  green: 'bg-green-100 text-green-900 border-green-500 hover:bg-green-200',
  emerald: 'bg-emerald-100 text-emerald-900 border-emerald-500 hover:bg-emerald-200',
  teal: 'bg-teal-100 text-teal-900 border-teal-500 hover:bg-teal-200',
  sky: 'bg-sky-100 text-sky-900 border-sky-500 hover:bg-sky-200',
  blue: 'bg-blue-100 text-blue-900 border-blue-500 hover:bg-blue-200',
  indigo: 'bg-indigo-100 text-indigo-900 border-indigo-500 hover:bg-indigo-200',
  violet: 'bg-violet-100 text-violet-900 border-violet-500 hover:bg-violet-200',
  purple: 'bg-purple-100 text-purple-900 border-purple-500 hover:bg-purple-200',
  pink: 'bg-pink-100 text-pink-900 border-pink-500 hover:bg-pink-200',
  rose: 'bg-rose-100 text-rose-900 border-rose-500 hover:bg-rose-200',
};

/** Fill classes for a calendar entry drawn in this color token; unknown tokens fall back to slate. */
export function calendarBlockClasses(token: string | null | undefined): string {
  return token && isColorToken(token) ? CALENDAR_BLOCK_CLASSES[token] : CALENDAR_BLOCK_CLASSES.slate;
}
